import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatRoute, isChannelId, loginRoute, VISIOUS_SCOPES, type Route } from '@visious/core';
import { changeSubscription } from '../api/auth';
import { describeAuthProblem, type ProblemCopy } from '../api/problem';
import { getPlatform } from '../platform/instance';
import { useSession } from '../platform/session';
import { useRoute } from '../router/router';
import { text } from '../strings';
import { loadSubscriptions, subscriptionsListKey } from './list';

/**
 * One channel's subscription state and the switch for it — what a subscribe button renders.
 *
 * The state is read off the shared subscription-list query (`subscriptions/list.ts`): the list is
 * names and ids, a few hundred bytes, and it is the *only* API answer to "am I subscribed?". Writes
 * update that cache in place — the row appears or disappears without waiting for a refetch — and then
 * invalidate it, so the truth still arrives.
 */
export interface SubscriptionBinding {
  /** `null` while it is not knowable: signed out, list still loading, or the id is not a UCID. */
  readonly subscribed: boolean | null;
  /** The control should exist at all — a collaboration byline has no single channel to subscribe to. */
  readonly available: boolean;
  readonly signedIn: boolean;
  /** `/login?next=<here>` — where a signed-out press should land. */
  readonly signInHref: string;
  readonly busy: boolean;
  /** `null` until a write has actually failed. Cleared by the next attempt. */
  readonly problem: ProblemCopy | null;
  readonly toggle: () => void;
}

/**
 * The write refusals, in words. `client` gets a specific sentence because it is *reachable*: the
 * endpoint validates existence, so any deleted channel answers
 * `403 {"error":"Deleted or invalid channel"}` — which classifies as `client`, and the generic
 * "our bug" copy would be wrong about it.
 */
function describeSubscribeProblem(problem: Parameters<typeof describeAuthProblem>[0]): ProblemCopy {
  const words = text().subscriptions.button;
  if (problem.kind === 'transport' && problem.error.kind === 'client') return words.invalidChannel;
  return describeAuthProblem(problem, {
    notFound: words.invalidChannel,
    unreadable: () => words.failed,
  });
}

export function useSubscription(channelId: string | null, route: Route): SubscriptionBinding {
  const session = useSession();
  const client = useQueryClient();
  const grantKey = session.grantedAtEpochSec ?? 0;
  const signedIn = session.ready && session.facts.signedIn;
  const usable = channelId !== null && isChannelId(channelId);

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ProblemCopy | null>(null);

  const listQuery = useQuery({
    queryKey: subscriptionsListKey(grantKey),
    queryFn: () => loadSubscriptions(),
    retry: false,
    // The set of channels you follow changes far less often than what they publish.
    staleTime: 30 * 60 * 1000,
    enabled: signedIn && usable,
  });

  const list = listQuery.data?.ok === true ? listQuery.data.subscriptions : null;
  const subscribed =
    !usable || list === null ? null : list.some((row) => row.channelId === channelId);

  const toggle = useCallback(() => {
    if (!usable || channelId === null || subscribed === null || busy) return;
    setBusy(true);
    setProblem(null);

    void (async () => {
      try {
        const boot = await getPlatform();
        if (!boot.ok) {
          setProblem(text().subscriptions.button.failed);
          return;
        }

        const action = subscribed ? 'unsubscribe' : 'subscribe';
        const result = await changeSubscription(boot.platform, action, channelId, VISIOUS_SCOPES);
        if (!result.ok) {
          setProblem(describeSubscribeProblem(result.error));
          return;
        }

        // In place first — the button flips now — then invalidated, so the instance's own answer
        // still replaces the local edit. The author name is not known here; the list query refetch
        // fills it in, and nothing renders the name off this row in the meantime.
        client.setQueryData<Awaited<ReturnType<typeof loadSubscriptions>>>(
          subscriptionsListKey(grantKey),
          (previous) =>
            previous?.ok !== true
              ? previous
              : {
                  ok: true,
                  subscriptions: subscribed
                    ? previous.subscriptions.filter((row) => row.channelId !== channelId)
                    : [...previous.subscriptions, { channelId, name: '' }],
                },
        );
        void client.invalidateQueries({ queryKey: subscriptionsListKey(grantKey) });
      } finally {
        setBusy(false);
      }
    })();
  }, [busy, channelId, client, grantKey, subscribed, usable]);

  return {
    subscribed,
    available: usable,
    signedIn,
    signInHref: SIGN_IN_HREF(route),
    busy,
    problem,
    toggle,
  };
}

function SIGN_IN_HREF(route: Route): string {
  return formatRoute(loginRoute(route));
}

/** The hook's usual companion: the route the sign-in should return to is the page the button is on. */
export function useSubscriptionHere(channelId: string | null): SubscriptionBinding {
  const route = useRoute();
  return useSubscription(channelId, route);
}
